import React, { useEffect, useReducer, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Plus, Save, Boxes, FileText, MessageSquare, Warehouse, ExternalLink } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import RequireAdmin from "@/components/admin/RequireAdmin";
import AdminShell from "@/components/admin/AdminShell";
import { toast } from "sonner";
import { useAdminI18n } from "@/lib/adminI18n";

/**
 * Admin → Shipping Only.
 *
 * This is a SEPARATE service from the Shipping Rates calculator (which
 * lives at /admin/shipping). Shipping Only manages:
 *   • the public /shipping-only page texts (en/ar/fr)
 *   • the SHEIN instructions block
 *   • an INDEPENDENT price list (NEVER merged with the calculator rates)
 *   • a link to the existing Warehouses CRUD page
 *
 * Backend: GET/PUT /api/admin/shipping-only-config
 * Public:  GET    /api/shipping-only-config (consumed by the embed +
 *                                            mobile app)
 */

const METHOD_OPTIONS = [
  { id: "air",   label: "Air" },
  { id: "sea",   label: "Sea" },
  { id: "land",  label: "Land" },
  { id: "shein", label: "SHEIN" },
  { id: "other", label: "Other" },
];

const UNIT_OPTIONS = [
  { id: "kg",  label: "per kg" },
  { id: "cbm", label: "per m³ (CBM)" },
];

const COUNTRY_OPTIONS = [
  { code: "",   label: "—" },
  { code: "LB", label: "Lebanon" },
  { code: "CN", label: "China" },
  { code: "AE", label: "UAE" },
  { code: "TR", label: "Turkey" },
  { code: "US", label: "USA" },
  { code: "GB", label: "UK" },
  { code: "FR", label: "France" },
];

// Editable page-text fields (must match the keys emitted by seed_data).
const PAGE_TEXT_FIELDS = [
  { key: "hero_eyebrow",  label: "Hero eyebrow"  },
  { key: "hero_title",    label: "Hero title"    },
  { key: "hero_subtitle", label: "Hero subtitle" },
  { key: "section_title", label: "Section title" },
  { key: "section_desc",  label: "Section description" },
  { key: "prices_title",  label: "Prices section title" },
  { key: "prices_desc",   label: "Prices section description" },
];

function newPrice() {
  return {
    id: `so-${Math.random().toString(36).slice(2, 10)}`,
    country: "",
    label: { en: "", ar: "", fr: "" },
    description: { en: "", ar: "", fr: "" },
    method: "air",
    price: 0,
    unit: "kg",
    eta: "",
    enabled: true,
    sort_order: 0,
  };
}

function I18nRow({ value, onChange, label, testidPrefix }) {
  const v = value || {};
  return (
    <div>
      <label className="block text-[11px] font-semibold tracking-wider uppercase text-white/40 mb-1.5">
        {label}
      </label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {["en", "ar", "fr"].map((lang) => (
          <input
            key={lang}
            placeholder={lang.toUpperCase()}
            value={v[lang] || ""}
            onChange={(e) => onChange({ ...v, [lang]: e.target.value })}
            dir={lang === "ar" ? "rtl" : "ltr"}
            className="h-9 px-3 rounded bg-black/40 border border-white/10 text-sm focus:border-[#F37021] focus:outline-none"
            data-testid={`${testidPrefix}-${lang}`}
          />
        ))}
      </div>
    </div>
  );
}

function AdminShippingOnlyPageInner() {
  const { apiFetch } = useAdminAuth();
  const { t, lang } = useAdminI18n();
  const copy = lang === "ar"
    ? {
        failedToLoad: "فشل تحميل إعدادات الشحن فقط",
        saved: "تم حفظ إعدادات الشحن فقط",
        addPrice: "إضافة سعر",
        pricesEmpty: "لا توجد أسعار بعد. أضف سعراً للبدء.",
        sheinHelp: "نص متعدد الأسطر يظهر في صفحة /shipping-only العامة بجانب بطاقة مستودع SHEIN.",
      }
    : {
        failedToLoad: "Failed to load Shipping Only config",
        saved: "Shipping Only saved",
        addPrice: "Add price",
        pricesEmpty: "No prices yet. Add a price to get started.",
        sheinHelp: "Multi-line copy shown on the public /shipping-only page next to the SHEIN warehouse card.",
      };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("prices");
  const [config, setConfig] = useState({
    page_texts: {},
    shein_instructions: { en: "", ar: "", fr: "" },
    prices: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/admin/shipping-only-config");
        const data = await res.json();
        if (cancelled) return;
        setConfig({
          page_texts: data.page_texts || {},
          shein_instructions: data.shein_instructions || { en: "", ar: "", fr: "" },
          prices: Array.isArray(data.prices) ? data.prices : [],
        });
      } catch {
        toast.error(copy.failedToLoad);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiFetch]);

  const onSave = async () => {
    const ids = config.prices.map((p) => (p.id || "").trim()).filter(Boolean);
    if (ids.length !== config.prices.length) {
      toast.error("Every price row needs an ID");
      return;
    }
    if (new Set(ids).size !== ids.length) {
      toast.error("Price IDs must be unique");
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch("/admin/shipping-only-config", {
        method: "PUT",
        body: JSON.stringify({
          page_texts: config.page_texts,
          shein_instructions: config.shein_instructions,
          prices: config.prices.map((p) => ({
            ...p,
            price: Number(p.price) || 0,
            sort_order: Number(p.sort_order) || 0,
            unit: p.unit || "kg",
            method: p.method || "air",
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Save failed");
      toast.success(copy.saved);
      setConfig({
        page_texts: data.page_texts || {},
        shein_instructions: data.shein_instructions || {},
        prices: data.prices || [],
      });
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updatePrice = (idx, patch) => {
    setConfig((c) => ({
      ...c,
      prices: c.prices.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    }));
  };
  const addPrice = () => {
    setConfig((c) => ({ ...c, prices: [...c.prices, newPrice()] }));
  };
  const removePrice = (idx) => {
    setConfig((c) => ({ ...c, prices: c.prices.filter((_, i) => i !== idx) }));
  };
  const updatePageText = (key, value) => {
    setConfig((c) => ({ ...c, page_texts: { ...c.page_texts, [key]: value } }));
  };

  if (loading) {
    return (
      <AdminShell
        title={t("shippingOnly.title")}
        description={t("shippingOnly.description")}
      >
        <div className="p-8 text-center text-white/60">{t("shippingOnly.loading")}</div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      title={t("shippingOnly.title")}
      description={t("shippingOnly.description")}
    >
      <div className="space-y-6">
        {/* Description banner */}
        <div className="admin-panel p-4 text-sm text-white/80">
          <div className="font-semibold text-[#F37021] mb-1">{t("shippingOnly.about")}</div>
          <p>{t("shippingOnly.aboutBody")}</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="so-tabs">
          {[
            { id: "prices",  label: t("shippingOnly.prices"), icon: Boxes },
            { id: "texts",   label: t("shippingOnly.pageTexts"), icon: FileText },
            { id: "shein",   label: t("shippingOnly.sheinInstructions"), icon: MessageSquare },
            { id: "wh",      label: t("shippingOnly.warehousesLink"), icon: Warehouse },
          ].map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-medium border transition ${
                  active
                    ? "bg-[#F37021] text-white border-[#F37021]"
                    : "bg-white/5 text-white/70 border-white/10 hover:border-[#F37021]/40"
                }`}
                data-testid={`so-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* PRICES TAB */}
        {activeTab === "prices" && (
          <section className="admin-panel p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold">{t("shippingOnly.title")} {t("shippingOnly.prices").toLowerCase()}</h2>
              <button
                type="button"
                onClick={addPrice}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#F37021] text-white text-sm font-medium hover:bg-[#d85f15]"
                data-testid="so-add-price"
              >
                <Plus className="w-4 h-4" /> {copy.addPrice}
              </button>
            </div>
            {config.prices.length === 0 ? (
              <p className="text-sm text-white/50">{copy.pricesEmpty}</p>
            ) : (
              <div className="space-y-3" data-testid="so-prices-list">
                {config.prices.map((p, idx) => (
                  <div
                    key={p.id || idx}
                    className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-3"
                    data-testid={`so-price-row-${p.id}`}
                  >
                    {/* Row 1: id, country, method, unit, price, eta, active, sort, delete */}
                    <div className="grid grid-cols-2 md:grid-cols-9 gap-2 items-end">
                      <div className="md:col-span-2">
                        <label className="block text-[11px] tracking-wider uppercase text-white/40 mb-1">ID</label>
                        <input
                          value={p.id || ""}
                          onChange={(e) => updatePrice(idx, { id: e.target.value })}
                          className="w-full h-9 px-2 rounded bg-black/40 border border-white/10 text-xs font-mono"
                          data-testid={`so-price-id-${idx}`}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] tracking-wider uppercase text-white/40 mb-1">Country</label>
                        <select
                          value={p.country || ""}
                          onChange={(e) => updatePrice(idx, { country: e.target.value })}
                          className="w-full h-9 px-2 rounded bg-black/40 border border-white/10 text-xs"
                          data-testid={`so-price-country-${idx}`}
                        >
                          {COUNTRY_OPTIONS.map((c) => (
                            <option key={c.code} value={c.code}>{c.code || "—"} {c.label !== "—" ? `· ${c.label}` : ""}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] tracking-wider uppercase text-white/40 mb-1">Method</label>
                        <select
                          value={p.method || "air"}
                          onChange={(e) => updatePrice(idx, { method: e.target.value })}
                          className="w-full h-9 px-2 rounded bg-black/40 border border-white/10 text-xs"
                          data-testid={`so-price-method-${idx}`}
                        >
                          {METHOD_OPTIONS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] tracking-wider uppercase text-white/40 mb-1">Price (USD)</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={p.price}
                          onChange={(e) => updatePrice(idx, { price: e.target.value })}
                          className="w-full h-9 px-2 rounded bg-black/40 border border-white/10 text-xs"
                          data-testid={`so-price-amount-${idx}`}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] tracking-wider uppercase text-white/40 mb-1">Unit</label>
                        <select
                          value={p.unit || "kg"}
                          onChange={(e) => updatePrice(idx, { unit: e.target.value })}
                          className="w-full h-9 px-2 rounded bg-black/40 border border-white/10 text-xs"
                          data-testid={`so-price-unit-${idx}`}
                        >
                          {UNIT_OPTIONS.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
                        </select>
                      </div>
                      <div className="col-span-2 md:col-span-1">
                        <label className="block text-[11px] tracking-wider uppercase text-white/40 mb-1">+ Combo (opt.)</label>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-white/40">+</span>
                          <input
                            type="number" step="0.1" min="0"
                            value={p.secondary_price ?? ""}
                            placeholder="—"
                            onChange={(e) => updatePrice(idx, { secondary_price: e.target.value === "" ? null : Number(e.target.value) })}
                            className="w-14 h-9 px-1 rounded bg-black/40 border border-white/10 text-xs"
                            data-testid={`so-price-secondary-${idx}`}
                            title="Optional combined secondary price (e.g. + $5.5/kg)"
                          />
                          <select
                            value={p.secondary_unit || ""}
                            onChange={(e) => updatePrice(idx, { secondary_unit: e.target.value || null })}
                            className="w-14 h-9 px-1 rounded bg-black/40 border border-white/10 text-xs"
                            data-testid={`so-price-secondary-unit-${idx}`}
                            title="Optional combined secondary unit"
                          >
                            <option value="">—</option>
                            <option value="kg">/kg</option>
                            <option value="cbm">/m³</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-[11px] tracking-wider uppercase text-white/40 mb-1">ETA</label>
                        <input
                          value={p.eta || ""}
                          placeholder="e.g. 20-25 days"
                          onChange={(e) => updatePrice(idx, { eta: e.target.value })}
                          className="w-full h-9 px-2 rounded bg-black/40 border border-white/10 text-xs"
                          data-testid={`so-price-eta-${idx}`}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] tracking-wider uppercase text-white/40 mb-1">Sort</label>
                        <input
                          type="number"
                          value={p.sort_order || 0}
                          onChange={(e) => updatePrice(idx, { sort_order: e.target.value })}
                          className="w-full h-9 px-2 rounded bg-black/40 border border-white/10 text-xs"
                          data-testid={`so-price-sort-${idx}`}
                        />
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs">
                          <input
                            type="checkbox"
                            checked={p.enabled !== false}
                            onChange={(e) => updatePrice(idx, { enabled: e.target.checked })}
                            data-testid={`so-price-enabled-${idx}`}
                          />
                          Active
                        </label>
                        <button
                          type="button"
                          onClick={() => removePrice(idx)}
                          className="p-2 rounded text-white/50 hover:text-red-400 hover:bg-red-500/10"
                          data-testid={`so-price-delete-${idx}`}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Row 2 — i18n labels */}
                    <I18nRow
                      value={p.label}
                      onChange={(v) => updatePrice(idx, { label: v })}
                      label="Label (shown on card)"
                      testidPrefix={`so-price-label-${idx}`}
                    />
                    {/* Row 3 — i18n description */}
                    <I18nRow
                      value={p.description}
                      onChange={(v) => updatePrice(idx, { description: v })}
                      label="Description (optional)"
                      testidPrefix={`so-price-desc-${idx}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* PAGE TEXTS TAB */}
        {activeTab === "texts" && (
          <section className="admin-panel p-5 space-y-5">
            <h2 className="font-display text-lg font-bold">{t("shippingOnly.publicPageTexts")}</h2>
            {PAGE_TEXT_FIELDS.map((f) => (
              <I18nRow
                key={f.key}
                value={config.page_texts[f.key]}
                onChange={(v) => updatePageText(f.key, v)}
                label={f.label}
                testidPrefix={`so-text-${f.key}`}
              />
            ))}
          </section>
        )}

        {/* SHEIN TAB */}
        {activeTab === "shein" && (
          <section className="admin-panel p-5 space-y-4">
            <h2 className="font-display text-lg font-bold">{t("shippingOnly.sheinInstructions")}</h2>
            <p className="text-xs text-white/50">
              {copy.sheinHelp}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {["en", "ar", "fr"].map((lang) => (
                <div key={lang}>
                  <label className="block text-[11px] tracking-wider uppercase text-white/40 mb-1">
                    {lang.toUpperCase()}
                  </label>
                  <textarea
                    value={config.shein_instructions?.[lang] || ""}
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        shein_instructions: { ...c.shein_instructions, [lang]: e.target.value },
                      }))
                    }
                    dir={lang === "ar" ? "rtl" : "ltr"}
                    rows={6}
                    className="w-full px-3 py-2 rounded bg-black/40 border border-white/10 text-sm focus:border-[#F37021] focus:outline-none"
                    data-testid={`so-shein-${lang}`}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* WAREHOUSES LINK TAB */}
        {activeTab === "wh" && (
          <section className="admin-panel p-5">
            <h2 className="mb-2 font-display text-lg font-bold">{t("shippingOnly.warehousesLink")}</h2>
            <p className="text-sm text-white/60 mb-4">
              Warehouse addresses, phone numbers, shipping marks, and notes
              are managed in their own section. Edits there reflect
              automatically on the public /shipping-only page.
            </p>
            <Link
              to="/admin/warehouses"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#F37021] text-white text-sm font-medium hover:bg-[#d85f15]"
              data-testid="so-wh-link"
            >
              <Warehouse className="w-4 h-4" /> {t("shippingOnly.openWarehouses")}
              <ExternalLink className="w-3.5 h-3.5 opacity-70" />
            </Link>
          </section>
        )}

        {/* Save bar (shown for prices/texts/shein tabs) */}
        {activeTab !== "wh" && (
          <div className="sticky bottom-4 z-20 flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#F37021] text-white text-sm font-semibold shadow-lg hover:bg-[#d85f15] disabled:opacity-50"
              data-testid="so-save"
            >
              <Save className="w-4 h-4" />
              {saving ? t("shippingOnly.loading") : t("shippingOnly.saveChanges")}
            </button>
          </div>
        )}
      </div>
    </AdminShell>
  );
}

export default function AdminShippingOnlyPage() {
  return (
    <RequireAdmin permission="content.manage">
      <AdminShippingOnlyPageInner />
    </RequireAdmin>
  );
}
